import { createStitches } from '@stitches/react';
import type * as Stitches from '@stitches/react';
import * as radixColors from '@radix-ui/colors';

export const COLORS = [
  'gray',
  'mauve',
  'slate',
  'sage',
  'olive',
  'sand',
  'tomato',
  'red',
  'crimson',
  'pink',
  'plum',
  'purple',
  'violet',
  'purple',
  'blue',
  'sky',
  'mint',
  'cyan',
  'teal',
  'green',
  'grass',
  'lime',
  'yellow',
  'amber',
  'orange',
  'brown',
  'bronze',
  'gold',
  'indigo',
] as const;
export type ColorType = (typeof COLORS)[number];
export type ColorStyles = { [key in ColorType]: any };

export const SIZES = ['small', 'medium', 'large', 'xlarge'] as const;
export type SizeType = (typeof SIZES)[number];
export type SizeStyles = { [key in SizeType]: any };

export const WEIGHTS = ['solid', 'solid-light', 'outlined', 'minimal'] as const;
export type WeightType = (typeof WEIGHTS)[number];
export type WeightStyles = { [key in WeightType]: any };

const getThemeColors = () => {
  const colors = {
    ...radixColors.blackA,
    ...radixColors.whiteA,
  };
  for (const color of COLORS) {
    Object.assign(colors, {
      ...radixColors[color],
      ...radixColors[`${color}A`],
    });
  }
  return colors;
};

export const { styled, css, globalCss, keyframes, getCssText, theme, createTheme, config } = createStitches({
  theme: {
    colors: getThemeColors(),
    space: {
      0: '0',
      1: '0.1rem',
      2: '0.25rem',
      3: '0.5rem',
      4: '0.75rem',
      5: '1rem',
      6: '1.25rem',
      7: '1.5rem',
      8: '2rem',
      9: '4rem',
      10: '6rem',
    },
    fontSizes: {
      1: '0.75rem',
      2: '0.875rem',
      3: '1rem',
      4: '1.25rem',
      5: '1.5rem',
      6: '2rem',
      7: '3rem',
      8: '4rem',
    },
    fonts: {
      sans: 'sans-serif',
      mono: 'monospace',
    },
    lineHeights: {
      1: 1,
      2: 1.25,
      3: 1.5,
    },
    letterSpacings: {
      none: '0',
      tight: '-.1em',
      loose: '.1em',
    },
    sizes: {
      0: '0',
      1: '0.1rem',
      2: '0.2rem',
      3: '0.3rem',
      4: '0.5rem',
      5: '0.75rem',
      6: '1rem',
      7: '1.5rem',
      8: '2rem',
      9: '4rem',
      10: '6rem',
    },
    borderWidths: {},
    borderStyles: {},
    radii: {
      small: '.25rem',
      medium: '0.5rem',
      full: '50%',
    },
    shadows: {
      small: '0 0 0 1px $colors$blackA2, 3px 3px 10px $colors$blackA5',
      flat: '-10px -10px 30px #fff, 10px 10px 30px #aeaec040',
      flatSmall: '-5px -5px 10px #fff, 5px 5px 10px #aeaec040',
      convex: `-10px -10px 30px #fff,
        10px 10px 30px #aeaec040,
        inset -10px -10px 10px #aeaec025,
        inset 10px 10px 10px #fff;`,
      convexSmall: `-6px -6px 10px #fff,
        6px 6px 10px #aeaec020,
        inset -6px -6px 6px #aeaec025,
        inset 6px 6px 6px #fff;`,
      concave: `inset -10px -10px 10px #ffffff70,
        inset 10px 10px 10px #aeaec020`,
      concaveSmall: `inset -3px -3px 3px #ffffff70,
        inset 3px 3px 3px #aeaec020`,
    },
    zIndices: {},
    transitions: {},
  },
  media: {
    small: '(min-width: 40em)',
    medium: '(min-width: 52em)',
    large: '(min-width: 64em)',
    xlarge: '(min-width: 100em)',
  },
  utils: {},
});

export type StitchesCSS = Stitches.CSS<typeof config>;

export type StyledProps<T = {}> = T & {
  css?: StitchesCSS;
};
